import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2, TrendingUp, MapPin, Megaphone } from 'lucide-react';

/**
 * Compact marketing-source dashboard rendered above the Leads grid.
 * Pulls aggregate counts from `GET /leads/source-stats` and shows three
 * leaderboards (source / type / event). Each row is click-to-filter on the
 * parent <LeadsPage>.
 */
export default function LeadSourceStatsPanel({ dateFrom = '', dateTo = '', activeSource = '', onPickSource }) {
  const { api } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (dateFrom) qs.set('date_from', dateFrom);
      if (dateTo) qs.set('date_to', dateTo);
      const { data } = await api.get(`/leads/source-stats?${qs.toString()}`);
      setStats(data);
    } catch { setStats(null); }
    setLoading(false);
  }, [api, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div data-testid="lead-source-stats-loading" className="bg-white border border-slate-200 rounded-xl px-4 py-3 text-xs text-slate-400 flex items-center gap-2">
        <Loader2 className="animate-spin" size={12} /> Loading marketing rollups…
      </div>
    );
  }

  if (!stats) return null;

  const bySource = stats.by_source || [];
  const byType   = stats.by_type   || [];
  const byEvent  = (stats.by_event || []).filter(r => r.key && r.key !== '(unspecified)');

  return (
    <section data-testid="lead-source-stats-panel" className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <header className="px-4 py-2.5 border-b border-slate-100 bg-slate-50 flex items-center gap-2">
        <TrendingUp size={13} className="text-[#D35400]" />
        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-600">Marketing rollups</h3>
        {activeSource && (
          <button data-testid="lead-source-stats-clear" onClick={() => onPickSource?.('')} className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200">
            Filtered: <strong>{activeSource}</strong> · clear ×
          </button>
        )}
      </header>
      <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-slate-100">
        <Leaderboard
          icon={<Megaphone size={11} />}
          title="By source channel"
          rows={bySource}
          activeKey={activeSource}
          onPick={onPickSource}
          testidPrefix="src"
        />
        <Leaderboard
          icon={<TrendingUp size={11} />}
          title="By lead type"
          rows={byType}
          testidPrefix="typ"
        />
        <Leaderboard
          icon={<MapPin size={11} />}
          title="By event / campaign"
          rows={byEvent}
          emptyHint="No leads tagged with an event yet"
          testidPrefix="evt"
        />
      </div>
    </section>
  );
}

const Leaderboard = ({ icon, title, rows, activeKey, onPick, emptyHint, testidPrefix }) => (
  <div className="p-3">
    <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1">{icon}{title}</h4>
    {rows.length === 0 ? (
      <p className="text-xs text-slate-300">{emptyHint || 'No data yet'}</p>
    ) : (
      <ul className="space-y-1">
        {rows.slice(0, 6).map(r => {
          const total = r.total || 0;
          const promoted = r.promoted || 0;
          const conv = total ? Math.round((promoted / total) * 100) : 0;
          const active = activeKey && r.key === activeKey;
          const clickable = !!onPick;
          return (
            <li key={r.key}>
              <button
                data-testid={`stat-${testidPrefix}-${(r.key || 'blank').replace(/\s+/g, '_').toLowerCase()}`}
                disabled={!clickable}
                onClick={() => clickable && onPick?.(r.key === activeKey ? '' : r.key)}
                className={`w-full flex items-center justify-between px-2 py-1 rounded-md text-xs transition-colors ${active ? 'bg-[#D35400]/10 text-[#D35400] font-semibold' : clickable ? 'hover:bg-slate-50 text-slate-700' : 'text-slate-700 cursor-default'}`}
              >
                <span className="truncate flex-1 text-left">{r.key || '(unspecified)'}</span>
                <span className="font-mono-num text-[11px] tabular-nums tracking-tight ml-2">{total}</span>
                {promoted > 0 && (
                  <span className="ml-1.5 px-1.5 py-0 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-semibold">{conv}%</span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    )}
  </div>
);
